import * as React from 'react';
import { useState } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@src/lib/utils';
import LoadingDots from '../loadingDots';
import Spinner from '../../loader/spinner';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground shadow hover:bg-primary/90',
        primary: 'bg-focus hover:bg-focus/90 text-white shadow',
        primarySquare:
          'bg-primary-dark text-white shadow hover:bg-primary-dark rounded-xl',
        secondary:
          'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80',
        black: 'bg-gray-1000 text-white shadow-sm hover:bg-gray-1000/90',
        destructive:
          'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
        outline:
          'border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        dangerOutline:
          'text-red-700 border border-red-700 hover:text-red-700/80 hover:border-red-700/80 active:text-red-700/80 active:border-red-700/80',
        danger:
          'text-red-800 hover:text-red-800/80 active:text-red-800/80 bg-red-100 hover:bg-red-100/90 active:bg-red-100/90',
        background:
          'bg-background-100 text-gray-1000 shadow-xs hover:bg-background-100/90 border border-gray-100',
        gray: 'bg-gray-100 text-gray-1000 hover:bg-gray-100/70 border border-gray-100',
      },
      size: {
        default: 'h-9 px-4 py-2',
        xs: 'h-6 px-2 text-[12px]',
        sm: 'h-8 px-3 text-xs',
        smTwo: 'px-4 py-3 text-sm',
        smThree: 'h-7 px-4 py-2 text-base',
        md: 'h-10 px-6 text-base',
        lg: 'h-12 px-8 text-lg',
        xl: 'h-[52px] px-10 text-base',
        xxl: 'h-[60px] px-12 text-base sm:text-lg font-normal',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface PressAndHoldConfig {
  onComplete: () => void; // eslint-disable-line
  duration?: number; // Duration in milliseconds (default: 2000ms)
  fillColor?: string; // Color for the fill animation (default: 'bg-focus')
}

// Custom easing function: Ultra-smooth start with gentle acceleration
const smoothEaseInOut = (t: number): number => {
  // Sine-based easing for silky smooth start and end
  return -(Math.cos(Math.PI * t) - 1) / 2;
};

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
  loadingType?: 'dots' | 'spinner';
  pressAndHold?: PressAndHoldConfig;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      isLoading = false,
      loadingType = 'spinner',
      pressAndHold,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';
    const [progress, setProgress] = useState<number>(0);
    const animationFrameRef = React.useRef<number | null>(null);
    const startTimeRef = React.useRef<number>(0);

    const duration = pressAndHold?.duration || 2000; // Default 2 seconds
    const fillColor = pressAndHold?.fillColor || 'bg-focus';

    const stopHolding = React.useCallback(() => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }

      // Get current progress and animate back to 0
      setProgress((currentProgress) => {
        if (currentProgress > 0 && currentProgress < 100) {
          // Start reverse animation
          const reverseStartTime = Date.now();
          const reverseStartProgress = currentProgress;
          const reverseDuration = 300; // Fast reverse animation

          const animateReverse = () => {
            const elapsed = Date.now() - reverseStartTime;
            const linearProgress = Math.min(elapsed / reverseDuration, 1);

            // Ease out for smooth deceleration
            const easedProgress = 1 - Math.pow(1 - linearProgress, 3);
            const newProgress = reverseStartProgress * (1 - easedProgress);

            setProgress(newProgress);

            if (newProgress > 0.5) {
              animationFrameRef.current = requestAnimationFrame(animateReverse);
            } else {
              setProgress(0);
            }
          };

          animationFrameRef.current = requestAnimationFrame(animateReverse);
        } else {
          setProgress(0);
        }
        return currentProgress;
      });
    }, []);

    const startHolding = React.useCallback(() => {
      if (!pressAndHold || isLoading || props.disabled) return;

      setProgress(0);
      startTimeRef.current = Date.now();

      const animate = () => {
        const elapsed = Date.now() - startTimeRef.current;
        const linearProgress = Math.min(elapsed / duration, 1);
        // Apply easing function for smooth acceleration/deceleration
        const easedProgress = smoothEaseInOut(linearProgress) * 100;

        setProgress(easedProgress);

        if (easedProgress >= 100) {
          if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
          }
          setProgress(100);
          pressAndHold.onComplete();
          // Quick fade out after completion
          setTimeout(() => setProgress(0), 200);
        } else {
          animationFrameRef.current = requestAnimationFrame(animate);
        }
      };

      animationFrameRef.current = requestAnimationFrame(animate);
    }, [pressAndHold, duration, isLoading, props.disabled]);

    const handleMouseDown = (e: React.MouseEvent<HTMLButtonElement>) => {
      startHolding();
      props.onMouseDown?.(e);
    };

    const handleMouseUp = (e: React.MouseEvent<HTMLButtonElement>) => {
      stopHolding();
      props.onMouseUp?.(e);
    };

    const handleMouseLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
      stopHolding();
      props.onMouseLeave?.(e);
    };

    const handleTouchStart = (e: React.TouchEvent<HTMLButtonElement>) => {
      startHolding();
      props.onTouchStart?.(e);
    };

    const handleTouchEnd = (e: React.TouchEvent<HTMLButtonElement>) => {
      stopHolding();
      props.onTouchEnd?.(e);
    };

    // Cleanup on unmount
    React.useEffect(() => {
      return () => {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
      };
    }, []);

    const renderLoadingIndicator = () => {
      if (!isLoading) return null;

      if (loadingType === 'dots') {
        return <LoadingDots color="primary" size="md" />;
      }
      if (loadingType === 'spinner') {
        return <Spinner size="w-5 h-5" />;
      }

      return null;
    };

    const renderContent = () => {
      if (isLoading) return renderLoadingIndicator();

      if (pressAndHold) {
        return (
          <span
            className={cn(
              'relative z-10 inline-flex items-center justify-center',
              progress > 60 && '!text-white'
            )}
            style={{
              transform: 'translateZ(0)',
              willChange: 'color',
              transition: 'color 0.35s ease-in-out',
            }}
          >
            {children}
          </span>
        );
      }

      return children;
    };

    const buttonProps = pressAndHold
      ? {
          ...props,
          onMouseDown: handleMouseDown,
          onMouseUp: handleMouseUp,
          onMouseLeave: handleMouseLeave,
          onTouchStart: handleTouchStart,
          onTouchEnd: handleTouchEnd,
          onClick: undefined, // Disable regular click when using press and hold
        }
      : props;

    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size }),
          pressAndHold && 'relative overflow-hidden',
          className
        )}
        ref={ref}
        disabled={isLoading || props.disabled}
        {...buttonProps}
      >
        {/* Press and Hold Progress Fill */}
        {pressAndHold && (
          <span
            className={cn(
              'absolute inset-0 z-0 rounded-[inherit]',
              !fillColor?.startsWith('#') && fillColor,
              progress === 0 && 'opacity-0'
            )}
            style={{
              width: `${progress}%`,
              opacity: progress > 0 ? 1 : 0,
              backgroundColor: fillColor?.startsWith('#')
                ? fillColor
                : undefined,
              transform: 'translateZ(0)', // Force GPU acceleration
              willChange: 'width, opacity',
              transition: 'opacity 0.2s ease-out',
            }}
          />
        )}

        {/* Button Content */}
        {renderContent()}
      </Comp>
    );
  }
);
Button.displayName = 'Button';

export default Button;
